import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ShoppingCart,
  Plus,
  Minus,
  X,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Select } from "./ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "./ui/sheet";
import { Alert, AlertTitle, AlertDescription } from "./ui/alert";
import Navigation from "./Navigation";
import {
  getProducts,
  getProductsByCategory,
  getProductsByCharacter,
  getCharacters,
} from "../services/api";
import QuickLook from "./QuickLook";

const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
  // Smart image path resolution - same logic as admin dashboard
  let imageSrc;
  if (item.mockupPaths && item.mockupPaths.length > 0) {
    imageSrc = item.mockupPaths[0];
  } else if (item.product && item.product.image) {
    imageSrc = item.product.image;
  } else if (item.image) {
    imageSrc = item.image;
  } else {
    // Fallback to hardcoded path only if no database path exists
    imageSrc = `/images/products/merch/${item.external_id}.jpg`;
  }

  return (
    <div className="flex items-center py-4">
      <img
        src={imageSrc}
        alt={item.name}
        className="h-20 w-20 rounded object-cover"
      />
    <div className="ml-4 flex-1">
      <h3 className="font-medium text-blue-900 dark:text-blue-100">
        {item.name}
      </h3>
      {(item.selectedSize || item.selectedColor) && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {item.selectedSize && `Size: ${item.selectedSize}`}
          {item.selectedSize && item.selectedColor && " | "}
          {item.selectedColor && `Color: ${item.selectedColor}`}
        </p>
      )}
      <p className="text-sm text-blue-600 dark:text-blue-400">
        ${item.price.toFixed(2)}
      </p>
      <div className="mt-2 flex items-center">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 dark:border-gray-600 dark:text-gray-200"
          onClick={() => onUpdateQuantity(item.cartItemId ?? item.id, item.quantity - 1)}
          disabled={item.quantity <= 1}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="mx-3 dark:text-gray-200">{item.quantity}</span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 dark:border-gray-600 dark:text-gray-200"
          onClick={() => onUpdateQuantity(item.cartItemId ?? item.id, item.quantity + 1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <Button
      variant="ghost"
      size="icon"
      className="ml-4 dark:text-gray-200 dark:hover:text-white"
      onClick={() => onRemove(item.cartItemId ?? item.id)}
    >
      <X className="h-4 w-4" />
    </Button>
  </div>
  );
};

CartItem.propTypes = {
  item: PropTypes.shape({
    cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    selectedSize: PropTypes.string,
    selectedColor: PropTypes.string,
  }).isRequired,
  onUpdateQuantity: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const CharacterCard = ({ character }) => (
  <Card className="mb-8 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
    <CardHeader>
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        <img
          src={character.image}
          alt={character.name}
          className="w-full md:w-1/3 h-48 md:h-auto rounded-lg object-contain bg-gray-50 dark:bg-gray-700"
        />
        <div>
          <CardTitle className="font-handwritten text-lg md:text-xl lg:text-2xl mb-3 md:mb-4 text-gray-900 dark:text-white">
            {character.name}
          </CardTitle>
          <CardDescription className="font-handwritten text-md md:text-lg lg:text-xl whitespace-pre-line text-gray-800 dark:text-gray-200 leading-relaxed">
            {character.description}
          </CardDescription>
        </div>
      </div>
    </CardHeader>
  </Card>
);

CharacterCard.propTypes = {
  character: PropTypes.shape({
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
};

const ProductCard = ({ product, variants, onAddToCart }) => {
  // Check if these are new Printful variants (with " / " in names) or existing products
  const hasVariantNaming = variants.some(v => v.name && v.name.includes(' / '));
  
  let sortedVariants, uniqueColors, uniqueSizes;
  
  if (hasVariantNaming) {
    // New Printful format: extract color and size from names like "Product / Color / Size"
    const variantsWithExtracted = variants.map(variant => {
      const parts = variant.name ? variant.name.split(' / ') : [];
      return {
        ...variant,
        extractedColor: parts[1] || '',
        extractedSize: parts[2] || variant.size || ''
      };
    });
    
    uniqueColors = [...new Set(variantsWithExtracted.map(v => v.extractedColor).filter(Boolean))];
    
    sortedVariants = variantsWithExtracted;
  } else {
    // Existing format: use existing size field and sorting
    sortedVariants = [...variants].sort((a, b) => {
      const sizeA = parseInt(a.size);
      const sizeB = parseInt(b.size);
      return sizeA - sizeB;
    });
    uniqueColors = [];
  }

  const [selectedColor, setSelectedColor] = useState(uniqueColors[0] || '');
  const [selectedSize, setSelectedSize] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const quickLookRef = React.useRef(null);
  
  // Compute available sizes based on selected color
  const availableSizes = React.useMemo(() => {
    if (hasVariantNaming) {
      const sizesForColor = sortedVariants
        .filter(v => !selectedColor || v.extractedColor === selectedColor)
        .map(v => v.extractedSize)
        .filter(Boolean);
      
      const uniqueSizesForColor = [...new Set(sizesForColor)];
      
      // Sort sizes for t-shirts (S, M, L, XL, etc.)
      const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];
      return uniqueSizesForColor.sort((a, b) => {
        const indexA = sizeOrder.indexOf(a);
        const indexB = sizeOrder.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        return a.localeCompare(b);
      });
    } else {
      // For existing products (coffee mugs), extract unique sizes and sort them numerically
      const uniqueSizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
      return uniqueSizes.sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB; // Numeric sort for sizes like "11oz", "15oz", "20oz"
        }
        return a.localeCompare(b); // Fallback to string comparison
      });
    }
  }, [hasVariantNaming, selectedColor, sortedVariants, variants]);
  
  // Update selected size when color changes and current size is not available
  React.useEffect(() => {
    if (hasVariantNaming && selectedColor && !availableSizes.includes(selectedSize)) {
      setSelectedSize(availableSizes[0] || '');
    }
  }, [hasVariantNaming, selectedColor, availableSizes, selectedSize]);
  
  // Initialize selected size when component mounts
  React.useEffect(() => {
    if (!selectedSize && availableSizes.length > 0) {
      setSelectedSize(availableSizes[0]);
    }
  }, [selectedSize, availableSizes]);
  
  // Compute selected variant based on current selections
  const selectedVariant = React.useMemo(() => {
    if (hasVariantNaming && (selectedColor || selectedSize)) {
      const matchingVariant = sortedVariants.find(v => 
        (!selectedColor || v.extractedColor === selectedColor) &&
        (!selectedSize || v.extractedSize === selectedSize)
      );
      return matchingVariant || sortedVariants[0];
    }
    return sortedVariants[selectedVariantIndex] || sortedVariants[0];
  }, [hasVariantNaming, selectedColor, selectedSize, sortedVariants, selectedVariantIndex]);

  // Get all images for the current variant with smart path resolution
  const getVariantImageSrc = (variant) => {
    if (variant.mockupPaths && variant.mockupPaths.length > 0) {
      return variant.mockupPaths[0];
    } else if (variant.product && variant.product.image) {
      return variant.product.image;
    } else if (variant.image) {
      return variant.image;
    } else {
      // Fallback to hardcoded path only if no database path exists
      return `/images/products/merch/${variant.external_id}.jpg`;
    }
  };

  const allImages = [
    getVariantImageSrc(selectedVariant),
    ...(selectedVariant.additionalImages || []),
  ];

  const handleAddToCart = () => {
    onAddToCart({
      ...selectedVariant,
      selectedSize: hasVariantNaming ? selectedVariant.extractedSize : selectedVariant.size,
      selectedColor: hasVariantNaming ? selectedVariant.extractedColor : selectedVariant.color,
      price:
        parseFloat(selectedVariant.retail_price) ||
        parseFloat(selectedVariant.price) ||
        0,
      printful: product.printful === true,
    });
  };
  
  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setCurrentImageIndex(0); // Reset image when color changes
  };

  const handleSizeSelect = (size) => {
    if (hasVariantNaming) {
      setSelectedSize(size);
      setCurrentImageIndex(0); // Reset image when size changes
    } else {
      // For existing products, find the variant index with this size
      const variantIndex = sortedVariants.findIndex(v => v.size === size);
      if (variantIndex !== -1) {
        setSelectedVariantIndex(variantIndex);
        setCurrentImageIndex(0);
      }
    }
  };

  const handlePrevImage = (e) => {
    e.preventDefault();
    setCurrentImageIndex((prev) =>
      prev === 0 ? allImages.length - 1 : prev - 1,
    );
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    setCurrentImageIndex((prev) =>
      prev === allImages.length - 1 ? 0 : prev + 1,
    );
  };



  // Get the price from retail_price with fallback to price or 0
  const variantPrice =
    parseFloat(selectedVariant?.retail_price) ||
    parseFloat(selectedVariant?.price) ||
    0;

  // Prepare product data for QuickLook
  const quickLookProduct = {
    ...selectedVariant,
    name: product.name,
    description: product.description || "No description available",
    price: variantPrice,
    image: allImages[currentImageIndex],
    additionalImages: allImages.slice(1),
    category: product.category || "General",
    character: product.character,
  };

  return (
    <div className="flex flex-col flex-1">
      <div className="aspect-square relative">
        <img
          src={allImages[currentImageIndex]}
          alt={`${product.name} view ${currentImageIndex + 1}`}
          className="object-cover w-full h-full cursor-pointer"
          onClick={() => quickLookRef.current?.click()}
        />
        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-1 rounded-full"
            >
              <ChevronLeft className="h-4 w-4 md:h-6 md:w-6" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-1 rounded-full"
            >
              <ChevronRight className="h-4 w-4 md:h-6 md:w-6" />
            </button>
          </>
        )}
      </div>
      <div className="p-4 md:p-5 flex-1">
        {/* Color Selection for new Printful format */}
        {hasVariantNaming && uniqueColors.length > 0 && (
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Color:</label>
            <Select 
              value={selectedColor} 
              onChange={(e) => handleColorSelect(e.target.value)}
              className="w-full"
            >
              <option value="">Select a color</option>
              {uniqueColors.map((color) => (
                <option key={color} value={color}>
                  {color}
                </option>
              ))}
            </Select>
          </div>
        )}
        
        {/* Size Selection */}
        {availableSizes.length > 0 && (
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Size:
            </label>
            <Select 
              value={hasVariantNaming ? selectedSize : selectedVariant.size} 
              onChange={(e) => handleSizeSelect(e.target.value)}
              className="w-full"
            >
              <option value="">Select a size</option>
              {availableSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="min-h-[4rem]">
          <p className="font-handwritten text-md md:text-lg lg:text-xl text-gray-800 dark:text-gray-200 leading-relaxed">
            {product.description || "No description available"}
          </p>
        </div>
      </div>
      <div className="p-4 md:p-5 border-t border-gray-100 dark:border-gray-700">
        <div className="text-lg font-bold text-gray-900 dark:text-white text-right mb-3">
          ${variantPrice.toFixed(2)}
        </div>
        <div className="flex gap-2 w-full">
          <Button
            onClick={handleAddToCart}
            className="flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm sm:text-base font-handwritten"
          >
            Add to Cart
          </Button>
          <QuickLook product={quickLookProduct} ref={quickLookRef} />
        </div>
      </div>
    </div>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    image: PropTypes.string.isRequired,
    additionalImages: PropTypes.arrayOf(PropTypes.string),
    category: PropTypes.string.isRequired,
    inStock: PropTypes.bool.isRequired,
    size: PropTypes.string,
    character: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        image: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }),
    ]),
  }).isRequired,
  variants: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddToCart: PropTypes.func.isRequired,
};

const ProductGroup = ({ character, products, onAddToCart }) => {
  const groupedProducts = products.reduce((acc, product) => {
    // Extract base name - if the name contains " / ", take the first part (new Printful format)
    // Otherwise, use the full name (existing products)
    const baseName = product.name && product.name.includes(' / ') 
      ? product.name.split(' / ')[0] 
      : product.name;
    
    if (!acc[baseName]) {
      acc[baseName] = [];
    }
    acc[baseName].push(product);
    return acc;
  }, {});

  return (
    <div className="mb-6 md:mb-8">
      <h2 className="text-lg md:text-xl lg:text-2xl font-bold mb-3 md:mb-4 text-blue-900 dark:text-blue-100 font-handwritten">
        {character} Collection
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5 md:gap-6 lg:gap-8">
        {Object.entries(groupedProducts).map(([baseName, variants]) => (
          <div
            key={baseName}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden flex flex-col"
          >
            <div className="min-h-[4.5rem] md:min-h-[5rem] lg:min-h-[5.5rem] px-4 pt-3 md:px-5 md:pt-4">
              <h3 className="text-md md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-blue-100 font-handwritten">
                {baseName}
              </h3>
            </div>
            <ProductCard
              key={variants[0].id}
              product={variants[0]}
              variants={variants}
              onAddToCart={onAddToCart}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductsPage = ({
  cartItems,
  setCartItems,
  handleUpdateQuantity,
  handleRemoveItem,
}) => {
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, charactersData] = await Promise.all([
          getProducts(),
          getCharacters(),
        ]);
        setAllProducts(productsData);
        setProducts(productsData);
        setCharacters(charactersData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const updateProducts = async () => {
      if (!selectedCharacter) {
        setProducts(allProducts);
        return;
      }

      setSectionLoading(true);
      try {
        let data;
        if (selectedCharacter === "stream") {
          data = await getProductsByCategory("Stream Merchandise");
        } else if (selectedCharacter === "other") {
          // Filter products that don't have character associations
          data = allProducts.filter(product => {
            const characterId = product.character?.id || product.character;
            const character = characters.find((char) => char.id === characterId);
            return !character; // Products without valid character associations
          });
        } else {
          data = await getProductsByCharacter(selectedCharacter);
        }
        setProducts(data);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.info("Error fetching products:", error);
        // Fallback to showing all products on error
        setProducts(allProducts);
      } finally {
        setSectionLoading(false);
      }
    };
    updateProducts();
  }, [selectedCharacter, allProducts, characters]);

  const handleAddToCart = (product) => {
    setCartItems((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const calculateTotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  };

  // Group products by character
  const groupProductsByCharacter = (products) => {
    if (process.env.NODE_ENV !== 'production') console.debug(
      "Grouping products:",
      products.map((p) => ({
        name: p.name,
        description: p.description,
        character: p.character,
      })),
    );

    const grouped = products.reduce((acc, product) => {
      // Get character from the product's character reference
      const characterId = product.character?.id || product.character;
      const character = characters.find((char) => char.id === characterId);
      const characterName = character?.name || "Other";

      if (!acc[characterName]) {
        acc[characterName] = [];
      }

      const groupedProduct = {
        ...product,
        // Use the cleaned name from the database directly
        name: product.name,
      };

      acc[characterName].push(groupedProduct);

      return acc;
    }, {});

    if (process.env.NODE_ENV !== 'production') console.debug(
      "Grouped products:",
      Object.entries(grouped).map(([char, prods]) => ({
        character: char,
        products: prods.map((p) => ({
          name: p.name,
          description: p.description,
        })),
      })),
    );

    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-blue-400 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">
            Loading products...
          </p>
        </div>
      </div>
    );
  }

  const groupedProducts = groupProductsByCharacter(products);
  const hasOtherProducts = groupedProducts["Other"] && groupedProducts["Other"].length > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {showNotification && (
        <div className="fixed bottom-4 right-4">
          <Alert className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <AlertTitle className="text-gray-900 dark:text-white">Success!</AlertTitle>
            <AlertDescription className="text-gray-600 dark:text-gray-300">
              Item added to cart
            </AlertDescription>
          </Alert>
        </div>
      )}
      <Navigation
        cartItems={cartItems}
        CartItem={CartItem}
        handleUpdateQuantity={handleUpdateQuantity}
        handleRemoveItem={handleRemoveItem}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />

      <div className="container mx-auto px-3 md:px-4 py-2">
        <div className="flex flex-col items-center mb-2">
          <img
            src="/ryscircus-logo.svg"
            alt="Ry's Circus Logo"
            className="h-40 w-auto mb-2"
          />
          <h1 className="text-3xl tracking-tight font-extrabold text-blue-900 dark:text-blue-100 sm:text-4xl md:text-5xl font-handwritten mb-6 md:mb-8">
            Circus Merch
          </h1>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-1.5 md:gap-2 mb-6 md:mb-8">
          <Button
            variant={!selectedCharacter ? "default" : "outline"}
            onClick={() => setSelectedCharacter(null)}
            className={`text-sm md:text-base font-handwritten ${
              !selectedCharacter
                ? ""
                : "text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
            }`}
            disabled={sectionLoading}
          >
            All Merch
          </Button>
          {characters.map((character) => (
            <Button
              key={character.id}
              variant={
                selectedCharacter === character.id ? "default" : "outline"
              }
              onClick={() => setSelectedCharacter(character.id)}
              className={`text-sm md:text-base font-handwritten ${
                selectedCharacter === character.id
                  ? ""
                  : "text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
              }`}
              disabled={sectionLoading}
            >
              {character.name}
            </Button>
          ))}
          {hasOtherProducts && (
            <Button
              variant={selectedCharacter === "other" ? "default" : "outline"}
              onClick={() => setSelectedCharacter("other")}
              className={`text-sm md:text-base font-handwritten ${
                selectedCharacter === "other"
                  ? ""
                  : "text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
              }`}
              disabled={sectionLoading}
            >
              Other
            </Button>
          )}
        </div>

        {/* Character Information */}
        {selectedCharacter && selectedCharacter !== "stream" && selectedCharacter !== "other" && (
          <CharacterCard
            character={characters.find((char) => char.id === selectedCharacter)}
          />
        )}

        {/* Product Groups */}
        {sectionLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 dark:border-blue-400 mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Updating products...
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupProductsByCharacter(products)).map(
              ([characterName, products]) => {
                // Show all groups when no character is selected
                if (!selectedCharacter) {
                  return (
                    <ProductGroup
                      key={characterName}
                      character={characterName}
                      products={products}
                      onAddToCart={handleAddToCart}
                    />
                  );
                }
                
                // Show only "Other" group when "other" is selected
                if (selectedCharacter === "other") {
                  return characterName === "Other" ? (
                    <ProductGroup
                      key={characterName}
                      character={characterName}
                      products={products}
                      onAddToCart={handleAddToCart}
                    />
                  ) : null;
                }
                
                // Show groups matching the selected character
                return (products[0]?.character?.id === selectedCharacter ||
                        products[0]?.character === selectedCharacter) ? (
                  <ProductGroup
                    key={characterName}
                    character={characterName}
                    products={products}
                    onAddToCart={handleAddToCart}
                  />
                ) : null;
              }
            )}
          </div>
        )}

        {/* Shopping Cart Sheet */}
        <Sheet>
          <SheetContent
            className="bg-white dark:bg-gray-800 w-full max-w-md"
            aria-describedby="products-cart-description"
          >
            <SheetHeader>
              <SheetTitle className="text-gray-900 dark:text-white">
                Shopping Cart
              </SheetTitle>
              <SheetDescription
                id="products-cart-description"
                className="text-gray-600 dark:text-gray-300"
              >
                Review and manage your selected items
              </SheetDescription>
            </SheetHeader>
            <div className="mt-8">
              {cartItems.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">
                  Your cart is empty
                </p>
              ) : (
                <>
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <CartItem
                        key={item.cartItemId ?? item.id}
                        item={item}
                        onUpdateQuantity={handleUpdateQuantity}
                        onRemove={handleRemoveItem}
                      />
                    ))}
                  </div>
                  <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex justify-between text-lg font-semibold">
                      <span className="text-gray-900 dark:text-blue-100">
                        Total
                      </span>
                      <span className="text-blue-600 dark:text-blue-400">
                        ${calculateTotal().toFixed(2)}
                      </span>
                    </div>
                    <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-handwritten">
                      Checkout
                    </Button>
                  </div>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

ProductsPage.propTypes = {
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      quantity: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      selectedSize: PropTypes.string,
      selectedColor: PropTypes.string,
    }),
  ).isRequired,
  setCartItems: PropTypes.func.isRequired,
  handleUpdateQuantity: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
};

export default ProductsPage;
